import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { HttpInterceptorParams } from '@app/core/interceptors/classes/http-interceptor-params';
import { IApiResponseError } from '@app/api/interfaces/common/api-response-error.interface';
import { BaseErrorStatus } from '@app/api/enums/base-error-status.enum';
import { DialogErrorInterceptedComponent } from '@app/core/interceptors/error-interceptor/dialogs/dialog-error-intercepted/dialog-error-intercepted.component';
import { HttpError } from '@app/core/interceptors/error-interceptor/classes/http-error';
import { baseErrorStatusLocalize } from '@app/api/localize/base-error-status';
import { DialogTrackerService } from '@app/core/services/dialog-tracker/dialog-tracker.service';
import { DialogForbiddenErrorInterceptedComponent } from '@app/core/interceptors/error-interceptor/dialogs/dialog-forbidden-error-intercepted/dialog-forbidden-error-intercepted.component';
import { HttpCancelService } from '@app/core/interceptors/cancel-pending-requests-interceptor/services/http-cancel.service';

export interface IErrorInterceptorMethodConfig {
  request: HttpRequest<unknown>;
  next: HttpHandlerFn;
  error: HttpErrorResponse;
  // interceptor: HttpInterceptor;
}

@Injectable({
  providedIn: 'root',
})
export class ErrorInterceptorService {
  private dialogTrackerService = inject(DialogTrackerService);
  private httpCancelService = inject(HttpCancelService);

  public handleDefaultError({ error }: IErrorInterceptorMethodConfig): Observable<HttpEvent<unknown>> {
    const config = this.dialogTrackerService.getBaseDialogConfig();

    this.dialogTrackerService.regAndOpen(DialogErrorInterceptedComponent, {
      ...config,
      maskStyleClass: `${config} !z-app-overlay-60`,
      data: new HttpError(
        error.error.message,
        JSON.stringify(error.error, null, 2),
        error.error.status,
        baseErrorStatusLocalize[error.error.status as BaseErrorStatus],
        error.status
      ),
    });

    return throwError(() => error);
  }

  public handleForbiddenError({ error }: IErrorInterceptorMethodConfig): Observable<HttpEvent<unknown>> {
    this.httpCancelService.cancelPendingRequests(true);

    const config = this.dialogTrackerService.getBaseDialogConfig();

    this.dialogTrackerService.regAndOpen(DialogForbiddenErrorInterceptedComponent, {
      ...config,
      maskStyleClass: `${config} !z-app-overlay-60`,
      data: new HttpError(
        error.error.message,
        JSON.stringify(error.error, null, 2),
        error.error.status,
        baseErrorStatusLocalize[error.error.status as BaseErrorStatus],
        error.status
      ),
    });

    return throwError(() => error);
  }

  public shouldBeIgnored(request: HttpRequest<unknown>, errorResponse: HttpErrorResponse): boolean {
    return (
      request.params instanceof HttpInterceptorParams &&
      (request.params.interceptorConfig.toIgnoreAll ||
        (!!request.params.interceptorConfig.toIgnore &&
          request.params.interceptorConfig.toIgnore.some(
            (item: { statusCodes?: HttpStatusCode[]; errors?: Partial<IApiResponseError<BaseErrorStatus>>[] }) => {
              let statusToIgnore = false;
              let errorsToIgnore = false;

              if (item.statusCodes) {
                statusToIgnore = item.statusCodes.includes(errorResponse.status);
              }

              if (item.errors) {
                errorsToIgnore = item.errors.some((error: Partial<IApiResponseError<BaseErrorStatus>>) => {
                  let coincidence = false;

                  for (const errorProp in error) {
                    if (Object.prototype.hasOwnProperty.call(error, errorProp)) {
                      const errorValue = error[errorProp as keyof IApiResponseError<BaseErrorStatus>];

                      if (
                        errorResponse.error &&
                        Object.prototype.hasOwnProperty.call(errorResponse, errorProp) &&
                        errorResponse.error[errorProp] === errorValue
                      ) {
                        coincidence = true;
                      } else {
                        coincidence = false;
                        break;
                      }
                    }
                  }

                  return coincidence;
                });
              }

              if (item.statusCodes && item.errors) {
                return statusToIgnore && errorsToIgnore;
              } else {
                return statusToIgnore || errorsToIgnore;
              }
            }
          )))
    );
  }
}
